/**
 * Copyright (c) 2025 eBUCIS, LLC. All rights reserved.
 *
 * Matching Card Component
 *
 * Individual card for the Math Term Matching Game. Supports states:
 * default, selected, correct, and incorrect with appropriate visual feedback.
 * Touch-friendly with 44px minimum tap target.
 *
 * This software is proprietary and confidential. Unauthorized copying,
 * distribution, or modification is strictly prohibited.
 */

const MatchingCard = ({ text, type, state, onClick, disabled, tabIndex = 0 }) => {
  const stateStyles = {
    default:
      'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-600 hover:shadow-md cursor-pointer',
    selected:
      'bg-primary-50 dark:bg-primary-900/30 border-primary-500 dark:border-primary-400 shadow-md ring-2 ring-primary-200 dark:ring-primary-800 cursor-pointer',
    correct:
      'bg-green-50 dark:bg-green-900/20 border-green-500 dark:border-green-400 cursor-default opacity-75',
    incorrect:
      'bg-red-50 dark:bg-red-900/20 border-red-500 dark:border-red-400 cursor-default animate-shake'
  }

  const textStyles = {
    default: 'text-gray-800 dark:text-gray-200',
    selected: 'text-primary-700 dark:text-primary-300',
    correct: 'text-green-700 dark:text-green-300',
    incorrect: 'text-red-700 dark:text-red-300'
  }

  const labelStyles = {
    term: 'bg-indigo-100 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-300',
    definition: 'bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-300'
  }

  const handleKeyDown = (e) => {
    if ((e.key === 'Enter' || e.key === ' ') && !disabled) {
      e.preventDefault()
      onClick()
    }
  }

  return (
    <div
      role="button"
      tabIndex={disabled ? -1 : tabIndex}
      onClick={disabled ? undefined : onClick}
      onKeyDown={handleKeyDown}
      aria-disabled={disabled}
      aria-label={`${type === 'term' ? 'Term' : 'Definition'}: ${text}`}
      data-testid={`matching-card-${type}`}
      className={`min-h-[44px] p-3 sm:p-4 border-2 rounded-xl transition-all duration-200 select-none ${stateStyles[state]} ${
        disabled && state === 'default' ? 'opacity-50 cursor-not-allowed' : ''
      }`}
    >
      <span
        className={`inline-block px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wider rounded mb-1.5 ${labelStyles[type]}`}
      >
        {type === 'term' ? 'Term' : 'Def'}
      </span>
      <p className={`text-sm font-medium leading-snug ${textStyles[state]}`}>{text}</p>
    </div>
  )
}

export default MatchingCard
